import React, { useCallback, useEffect } from 'react';
import { Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import AccountCircleIcon from '../../../assets/svg/account_circle.svg?react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  fetchBusinessProfileContactById,
  selectBusinessProfileContacts,
  selectBusinessProfileStatus,
} from '../../slices/business-profile/businessProfileSlice';
import styles from './OrganizationInfoCard.module.scss';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { fetchCompanies, selectAllCompanies } from '../../slices/companies/companiesSlice';

export default function OrganizationInfoCard() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];

  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);
  const businessProfileContacts = useAppSelector(selectBusinessProfileContacts);

  const getContacts = useCallback(() => {
    dispatch(fetchBusinessProfileContactById({ id: company?.id ?? 0 }));
  }, [company?.id, dispatch]);

  useEffect(() => {
    if (!company) {
      dispatch(fetchCompanies());
    }
  }, [dispatch, company]);

  useEffect(() => {
    getContacts();
  }, [getContacts]);

  const isLoading = businessProfileStatus === 'loading';

  const directors =
    businessProfileContacts?.filter((contact) => contact.contact_position === 'Director') ?? [];
  const accountants =
    businessProfileContacts?.filter((contact) => contact.contact_position === 'Accountant') ?? [];
  const secretaries =
    businessProfileContacts?.filter((contact) => contact.contact_position === 'Secretary') ?? [];

  return (
    <Grid component='div' container className={styles.card_content_height}>
      <Grid component='div' size={{ xs: 12 }}>
        <Card className={`inputs-editable ${styles.card} ${styles.card_content_height}`}>
          <CardContent
            className={isLoading ? styles.card_content_min_height : styles.card_content_height}
            sx={{ paddingTop: 0, paddingBottom: isLoading ? '0 !important' : '16px !important' }}
          >
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Grid
                  component='div'
                  container
                  style={{
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    minHeight: '76px',
                    paddingTop: '16px !important',
                    paddingBottom: '16px !important',
                    paddingLeft: '0 !important',
                    paddingRight: '0 !important',
                  }}
                >
                  <Grid
                    component='div'
                    size={{ xs: 12 }}
                    container
                    style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
                  >
                    <AccountCircleIcon />
                    <Typography variant='h6' className='semibold-variant'>
                      {t('businessProfile.organizationCardHeader')}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid component='div' container>
                  <Grid component='div' size={{ xs: 12, sm: 4 }}>
                    <Typography variant='subtitle1'>{t('businessProfile.director')}</Typography>
                    {directors.length > 0 ? (
                      directors.map((contact) => (
                        <Typography key={contact.id}>{contact.contact_name}</Typography>
                      ))
                    ) : (
                      <Typography color='textSecondary'>
                        {t('businessProfile.noDirector')}
                      </Typography>
                    )}
                  </Grid>

                  <Grid component='div' size={{ xs: 12, sm: 4 }}>
                    <Typography variant='subtitle1'>{t('businessProfile.accountant')}</Typography>
                    {accountants.length > 0 ? (
                      accountants.map((contact) => (
                        <Typography key={contact.id}>{contact.contact_name}</Typography>
                      ))
                    ) : (
                      <Typography color='textSecondary'>
                        {t('businessProfile.noAccountant')}
                      </Typography>
                    )}
                  </Grid>

                  <Grid component='div' size={{ xs: 12, sm: 4 }}>
                    <Typography variant='subtitle1'>{t('businessProfile.secretary')}</Typography>
                    {secretaries.length > 0 ? (
                      secretaries.map((contact) => (
                        <Typography key={contact.id}>{contact.contact_name}</Typography>
                      ))
                    ) : (
                      <Typography color='textSecondary'>
                        {t('businessProfile.noSecretary')}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
